/**
 * /docs/connections — connect a private resource. Plain-English guide for
 * non-developers: add a connection in the app's Connections tab, run the
 * one-time install command on your own server, then call it from the app.
 */

import { Mono, PageHeader } from '../../components/ui.js';
import { DocsTabs } from '../../components/DocsTabs.js';
import { Callout, CodeBlock, DocSection } from './_shared.js';

export function DocsConnections() {
  return (
    <div className="animate-fade-in">
      <PageHeader
        eyebrow="Docs · Private connections"
        title="Connect a private resource"
        description="Let an app reach something on your own server or VM that isn't on the public internet."
      />

      <DocsTabs />

      <DocSection title="What it is">
        <p>
          A connection lets your app talk to something running on your own server or VM &mdash; a
          database admin tool, an internal API, a service that only listens on the office network.
          Things that aren&apos;t reachable from the public internet. You install a small connector
          on that machine; your app then reaches the service by name, as if it were next door.
        </p>
      </DocSection>

      <DocSection id="add" title="Add a connection">
        <p>
          Open your app and go to the <strong>Connections</strong> tab, then click{' '}
          <strong>Create</strong>. You give it two things:
        </p>
        <ul className="list-disc list-inside space-y-1 pl-1">
          <li>
            <strong>A name</strong> &mdash; how your app will refer to it, e.g.{' '}
            <Mono>warehouse</Mono>.
          </li>
          <li>
            <strong>The local address it runs at</strong> &mdash; where the service answers on that
            machine, e.g. <Mono>http://localhost:8080</Mono>.
          </li>
        </ul>
      </DocSection>

      <DocSection id="connect" title="Connect your server">
        <p>
          After you create the connection, Kin shows you a single command. Copy it and run it on the
          server where your service lives. It needs admin rights (<Mono>sudo</Mono> on Linux). The
          command installs a small connector that only makes <em>outbound</em> connections &mdash;
          no firewall changes, no open ports, nothing for an attacker to reach.
        </p>
        <Callout tone="tip" title="The command is shown only once.">
          <p>
            It contains a secret token that isn&apos;t displayed again. Copy it the moment it
            appears. If you lose it, delete the connection and create a new one.
          </p>
        </Callout>
        <p>
          Once the connector is running, the connection&apos;s status flips from{' '}
          <strong>Pending</strong> to <strong>Connected</strong> on its own &mdash; usually within a
          few seconds. Wait for <strong>Connected</strong> before using it.
        </p>
      </DocSection>

      <DocSection id="use" title="Use it from your app">
        <p>
          Refer to the connection by the name you gave it. Your app reaches the service through it
          the same way it would call any web address:
        </p>
        <CodeBlock
          body={`const res = await this.connection("warehouse").fetch("/inventory");
const data = await res.json();`}
          caption="inside your app"
        />
        <p>
          The path after the name (<Mono>/inventory</Mono>) is whatever path your service answers
          on. When you build with the AI, just tell it which connection to use and what to fetch
          &mdash; it writes this for you.
        </p>
      </DocSection>

      <DocSection id="troubleshooting" title="Troubleshooting">
        <Callout title="Stuck on Pending">
          <p>
            The connector isn&apos;t running. Re-run the install command on the server, and check it
            finished without errors.
          </p>
        </Callout>
        <Callout title="Connected, but “service not responding”">
          <p>
            The connector is up, but nothing answered at the address you gave. Make sure your
            service is actually running and listening on that exact address and port (the{' '}
            <Mono>http://localhost:8080</Mono> you entered).
          </p>
        </Callout>
        <Callout title="On a locked-down server">
          <p>
            The connector needs to make outbound HTTPS connections on port <strong>443</strong>. For
            best performance it also uses UDP port <strong>7844</strong>. If the install command
            hangs or never connects, ask whoever manages the network to allow that outbound traffic.
          </p>
        </Callout>
      </DocSection>

      <DocSection id="databases" title="Databases & other non-web services">
        <p>
          A connection only carries web (HTTP) traffic. A database, a message queue, or anything
          that doesn&apos;t speak HTTP can&apos;t be reached directly. The fix: run a small web
          endpoint on the same server, sitting in front of the service, and point the connection at
          that endpoint. The endpoint takes web requests and talks to the database for you. If you
          ask the AI for help, it can usually write that little endpoint too.
        </p>
      </DocSection>

      <DocSection id="remove" title="Remove it">
        <p>Removing a connection is two steps:</p>
        <ol className="ml-5 list-decimal space-y-1">
          <li>
            In the <strong>Connections</strong> tab, click <strong>Delete</strong>. This removes the
            tunnel and its credentials from Kin. Any calls your app makes to that connection stop
            working immediately.
          </li>
          <li>
            Kin then shows an uninstall command. The connector keeps running on your machine until
            you remove it &mdash; run the command (it needs admin rights) on the same machine where
            you installed it:
            <CodeBlock body="sudo /usr/local/bin/cloudflared service uninstall" />
          </li>
        </ol>
        <Callout>
          If Kin couldn&rsquo;t fully clean up on its side, it tells you exactly what to remove.
          That only happens if Cloudflare was briefly unreachable during delete.
        </Callout>
      </DocSection>
    </div>
  );
}
